import React, { useCallback, useEffect, useState } from "react";
import MainBody from "../../components/MainBody/MainBody";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  fileUpload,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import {
  formatContactName,
  formatNumber,
  pastFinancialdate,
} from "../../lib/Misc";
import { Download, Home } from "lucide-react";
import { MonthRangePicker } from "../../components/ui/monthrangepicker";
import { useMediaQuery } from "../../components/use-media-query";
import CommonFilters from "../../components/CommonFilters";
import { useTrust } from "../../lib/context/TrustContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Card, CardContent } from "../../components/ui/card";
import { useOrganization } from "../../lib/context/OrganisationContext";
import DropDownButton from "../../components/DropDownButton";
import { cn } from "../../lib/utils";

const GeneralLedger = ({ org = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const fromMonth = query.get("from")
    ? new Date(
        `${query.get("from")?.split("-")[1]}-${
          query.get("from")?.split("-")[0]
        }-01`
      )
    : "";
  const toMonth = query.get("to")
    ? new Date(
        `${query.get("to")?.split("-")[1]}-${query.get("to")?.split("-")[0]}-01`
      )
    : "";
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const { trustId: trustID, trustData } = useTrust(org);
  const [trustId, setTrustId] = useState(org ? "" : trustID);
  const [selectedMonthRange, setSelectedMonthRange] = React.useState(
    fromMonth && toMonth
      ? {
          start: fromMonth,
          end: toMonth,
        }
      : {
          start: pastFinancialdate[0],
          end: pastFinancialdate[1],
        }
  );
  const { organizationData } = useOrganization();

  const [vendorId, setVendorId] = useState();
  const [contactId, setContactId] = useState();
  
  const [trustListData, setTrustListData] = useState([]);

  const [activeTrustId, setActiveTrustId] = React.useState(null);

  useEffect(() => {
    setTrustId(trustID);
  }, [trustID]);

  const fetchTustGeneralLedgerReportData = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (trustId?.length > 0) {
        trustId?.map((item, index) =>
          formData.append(`trust_ids[${index}]`, item)
        );
      }

      formData.append("type", "general-ledger-report");
      formData.append(
        "organization_id",
        organizationData?.organizationId ? organizationData?.organizationId : ""
      );
      formData.append(
        `from`,
        moment(selectedMonthRange?.start).format("MM-YYYY")
          ? moment(selectedMonthRange?.start).format("MM-YYYY")
          : ""
      );
      formData.append(
        `to`,
        moment(selectedMonthRange?.end).format("MM-YYYY")
          ? moment(selectedMonthRange?.end).format("MM-YYYY")
          : ""
      );

      const { data } = await fileUpload(
        `${API.ORG_GENERAL_LEDGER_REPORT}`,
        formData
      );
      setTrustListData(data?.data);
      let activetTust = data?.data?.filter((item) => item?.transactions === 1);

      if (activetTust?.[0]?.id !== undefined) {
        setActiveTrustId(activetTust?.[0]?.id);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setReportData([]);
    }
  }, [
    trustId,
    organizationData?.organizationId,
    selectedMonthRange?.start,
    selectedMonthRange?.end,
  ]);

  useEffect(() => {
    if (org) {
      fetchTustGeneralLedgerReportData();
    }
  }, [org, fetchTustGeneralLedgerReportData]);

  const handleAccordionChange = async (value) => {
    setReportData([]);
    if (!value) {
      setActiveTrustId(null);
      return;
    }
    setActiveTrustId(value);
  };

  const fetchGeneralLedgerReportData = useCallback(
    async (activeTrustId) => {
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append(
          `trust_id`,
          org && activeTrustId ? activeTrustId : trustId ? trustId : ""
        );
        formData.append(
          `from`,
          moment(selectedMonthRange?.start).format("MM-YYYY")
            ? moment(selectedMonthRange?.start).format("MM-YYYY")
            : ""
        );
        formData.append(
          `to`,
          moment(selectedMonthRange?.end).format("MM-YYYY")
            ? moment(selectedMonthRange?.end).format("MM-YYYY")
            : ""
        );

        if (vendorId?.length > 0) {
          vendorId?.map((item, index) =>
            formData.append(`vendor_ids[${index}]`, item)
          );
        }

        if (contactId?.length > 0) {
          contactId?.map((item, index) =>
            formData.append(`contact_ids[${index}]`, item)
          );
        }

        const { data } = await fileUpload(
          `${API.GENERAL_LEDGER_REPORT}`,
          formData
        );
        setReportData(data?.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setReportData([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedMonthRange?.start,
      selectedMonthRange?.end,
      vendorId,
      trustId,
      contactId,
    ]
  );

  useEffect(() => {
    if (org) {
      if (activeTrustId) {
        fetchGeneralLedgerReportData(activeTrustId);
      }
    } else if (!org) {
      fetchGeneralLedgerReportData();
    }
  }, [activeTrustId, fetchGeneralLedgerReportData, org]);

  /**
   * Table Change For Sorting
   */
  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    if (!org) {
      navigate(
        `?sort_column=${sortField ? sortField : ""}&sort_order=${
          sortOrder ? sortOrder : ""
        }&from=${
          moment(selectedMonthRange?.start).format("MM-YYYY")
            ? moment(selectedMonthRange?.start).format("MM-YYYY")
            : ""
        }&to=${
          moment(selectedMonthRange?.end).format("MM-YYYY")
            ? moment(selectedMonthRange?.end).format("MM-YYYY")
            : ""
        }`,
        {
          replace: true,
        }
      );
    }
  }, [
    navigate,
    selectedMonthRange?.end,
    selectedMonthRange?.start,
    sortField,
    sortOrder,
    org,
  ]);

  const reportGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      i > 0 &&
        items.push({
          key: `blank-row-${i}`,
          blankRow: 1,
          date: "",
          audit_number: "",
          description: "",
          corresponding_account: "",
          value: "",
          balance: "",
        });
      items.push({
        key: i,
        balanceRow: 1,
        date: "",
        audit_number: "",
        description: formatContactName(
          reportData[i]?.type,
          {
            last_name: reportData[i]?.name,
          },
          organizationData?.organizationData
        ),
        corresponding_account: "",
        value: "Opening Balance",
        balance: reportData[i]?.opening_balance
          ? reportData[i]?.opening_balance > 0
            ? `($${formatNumber(Number(reportData[i]?.opening_balance))})`
            : `$${formatNumber(
                Math.abs(Number(reportData[i]?.opening_balance))
              )}`
          : "$0.00",
      });

      for (let j = 0; j < reportData[i]?.transactions?.length; j++) {
        items.push({
          key: `${i}-${j}`,
          date: reportData[i]?.transactions[j]?.transaction_date
            ? moment(
                reportData[i]?.transactions[j]?.transaction_date,
                "DD-MM-YYYY"
              ).format("DD-MM-YYYY")
            : "",
          audit_number: reportData[i]?.transactions[j]?.audit_number
            ? reportData[i]?.transactions[j]?.audit_number
            : "",
          description: reportData[i]?.transactions[j]?.description
            ? reportData[i]?.transactions[j]?.description
            : "",
          is_reversed: reportData[i]?.transactions[j]?.is_reversed,
          corresponding_account: !reportData[i]?.transactions[j]
            ?.transaction_type ? (
            <span
            // className={
            //   reportData[i]?.transactions[j]?.transaction_type
            //     ? "text-green-500"
            //     : "text-red-500"
            // }
            >
              {reportData[i]?.transactions[j]?.amount
                ? `$${formatNumber(
                    Number(reportData[i]?.transactions[j]?.amount)
                  )}`
                : "$0.00"}
            </span>
          ) : (
            "-"
          ),
          value: reportData[i]?.transactions[j]?.transaction_type ? (
            <span
            // className={
            //   reportData[i]?.transactions[j]?.transaction_type
            //     ? "text-green-500"
            //     : "text-red-500"
            // }
            >
              {reportData[i]?.transactions[j]?.amount
                ? `$${formatNumber(
                    Number(reportData[i]?.transactions[j]?.amount)
                  )}`
                : "$0.00"}
            </span>
          ) : (
            "-"
          ),
          balance: reportData[i]?.transactions[j]?.closing_balance
            ? reportData[i]?.transactions[j]?.closing_balance > 0
              ? `($${formatNumber(
                  Number(reportData[i]?.transactions[j]?.closing_balance)
                )})`
              : `$${formatNumber(
                  Math.abs(
                    Number(reportData[i]?.transactions[j]?.closing_balance)
                  )
                )}`
            : "$0.00",
        });
        reportData[i]?.transactions?.length - 1 === j &&
          items.push({
            key: `${i}-${j}-close`,
            balanceRow: 1,
            date: "",
            audit_number: "",
            description: "",
            corresponding_account: "",
            value: "Closing Balance",
            balance: reportData[i]?.closing_balance
              ? reportData[i]?.closing_balance > 0
                ? `($${formatNumber(Number(reportData[i]?.closing_balance))})`
                : `$${formatNumber(
                    Math.abs(Number(reportData[i]?.closing_balance))
                  )}`
              : "$0.00",
          });
      }

      if(reportData[i]?.transactions?.length === 0) {
        items.push({
            key: `${i}-close`,
            balanceRow: 1,
            date: "",
            audit_number: "",
            description: "",
            corresponding_account: "",
            value: "Closing Balance",
            balance: reportData[i]?.closing_balance
              ? reportData[i]?.closing_balance > 0
                ? `($${formatNumber(Number(reportData[i]?.closing_balance))})`
                : `$${formatNumber(
                    Math.abs(Number(reportData[i]?.closing_balance))
                  )}`
              : "$0.00",
          });
      }
    }
    return items;
  };

  const reportDatas = reportGenerator(reportData?.length);

  /**
   * Table Heading Data
   */
  const columns = [
    {
      dataField: "date",
      text: "Date",
      headerClasses: `${THClasses} !rounded-none`,
      classes: (cell, row) => {
        if (row?.blankRow) {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "audit_number",
      text: "Reference/ID",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.blankRow) {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "description",
      text: "Transaction Details",
      headerClasses: cn(
        THClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      classes: (cell, row) => {
        if (row?.blankRow) {
          return cn(
            TDClasses,
            "whitespace-normal break-words border-r-0" // fixed width + wrapping
          );
        }
        return cn(
          TDClasses,
          "whitespace-normal break-words" // fixed width + wrapping
        );
      },
    },
    {
      dataField: "corresponding_account",
      text: "Debit",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.blankRow) {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "value",
      text: "Credit",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.blankRow) {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "balance",
      text: "Balance",
      headerClasses: `${THClasses} !rounded-none`,
      classes: TDClasses,
    },
  ];

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const formData = new FormData();

      if (org) {
        if (trustId?.length > 0) {
          trustId?.map((item, index) =>
            formData.append(`trust_ids[${index}]`, item)
          );
        }

        formData.append(
          "organization_id",
          organizationData?.organizationId
            ? organizationData?.organizationId
            : ""
        );
      } else if (!org) {
        formData.append(`trust_id`, trustId ? trustId : "");
      }
      formData.append(`export`, 1);
      formData.append("format", format);
      formData.append(
        `from`,
        moment(selectedMonthRange?.start).format("MM-YYYY")
          ? moment(selectedMonthRange?.start).format("MM-YYYY")
          : ""
      );
      formData.append(
        `to`,
        moment(selectedMonthRange?.end).format("MM-YYYY")
          ? moment(selectedMonthRange?.end).format("MM-YYYY")
          : ""
      );

      if (vendorId?.length > 0) {
        vendorId?.map((item, index) =>
          formData.append(`vendor_ids[${index}]`, item)
        );
      }

      if (contactId?.length > 0) {
        contactId?.map((item, index) =>
          formData.append(`contact_ids[${index}]`, item)
        );
      }

      const { data } = await fileUpload(
        `${API.GENERAL_LEDGER_REPORT}`,
        formData
      );
      const export_report_path = data?.data?.file;
      if (export_report_path) {
        var a = document.createElement("a");
        a.href = export_report_path;
        var file = export_report_path.split("/");
        a.setAttribute(
          "download",
          file[file.length - 1] || "transactionsreport.pdf"
        );
        a.setAttribute("target", "_blank");
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove();
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        search={false}
        actionRight={isDesktop ? true : false}
        actionContent={
          <>
            <MonthRangePicker
              onMonthRangeSelect={setSelectedMonthRange}
              selectedMonthRange={selectedMonthRange}
              variant="outline"
              align={isDesktop ? "end" : "start"}
              maxDate={new Date()}
            />
            <CommonFilters
              trust={org}
              trustId={trustId}
              multiTrust={org}
              setTrustId={setTrustId}
              vendor={true}
              setVendorId={setVendorId}
              vendorId={vendorId}
              multiVendor={true}
              multiContact={true}
              contact={true}
              setContactId={setContactId}
              contactId={contactId}
              // setTrustListData={setTrustListData}
            />

            <DropDownButton
              items={[
                {
                  label: "PDF",
                  onClick: () => handleExport("pdf"),
                  disabled: loading,
                },
                {
                  label: "Excel",
                  onClick: () => handleExport("xlsx"),
                  disabled: loading,
                },
              ]}
              text={
                <div className="flex gap-2 items-center">
                  Export <Download size={18} />
                </div>
              }
            />
          </>
        }
        loading={loading}
      >
        {org && trustListData?.length > 0 ? (
          <>
            <Accordion
              type="single"
              collapsible
              value={activeTrustId}
              onValueChange={handleAccordionChange}
              className="w-full"
            >
              {trustListData.map((item) => {
                const trustLabel = item?.business_name
                  ? item?.business_name
                  : item?.first_name
                  ? item?.first_name + " " + item?.last_name
                  : item?.last_name;

                return (
                  <AccordionItem
                    key={item?.id}
                    value={item?.id}
                    className="border-none mb-2"
                  >
                    <AccordionTrigger className="text-base font-medium bg-webSecondary hover:bg-webSecondary text-white dark:bg-muted px-4 py-2 rounded-md dark:hover:bg-muted/70">
                      <div className="flex items-center justify-between w-full pr-3">
                        {trustLabel}
                        <div className="flex gap-3">
                          <div>
                            Opening Balance:{" "}
                            <span>
                              {item?.opening_balance
                                ? item?.opening_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.opening_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.opening_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                          <div>
                            Closing Balance:{" "}
                            <span>
                              {item?.closing_balance
                                ? item?.closing_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.closing_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.closing_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="custom-table mt-2">
                        <div
                          className={
                            "table-body-class sm:max-h-[600px] rounded-md"
                          }
                        >
                          <table className="w-full">
                            <thead>
                              <tr className={TRClasses}>
                                {reportData && (
                                  <th className={`${THClasses} text-center`}>
                                    {organizationData?.organizationData
                                      ?.business_name +
                                      " - " +
                                      (item?.business_name
                                        ? item?.business_name
                                        : item?.first_name
                                        ? item?.first_name +
                                          " " +
                                          item?.last_name
                                        : item?.last_name)}{" "}
                                    Trust
                                  </th>
                                )}
                              </tr>
                              <tr>
                                <th
                                  className={`${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white`}
                                >
                                  Ledger Account for period from{" "}
                                  {moment(selectedMonthRange?.start).format(
                                    "MMM-YYYY"
                                  )
                                    ? moment(selectedMonthRange?.start).format(
                                        "MMM-YYYY"
                                      )
                                    : ""}{" "}
                                  to{" "}
                                  {moment(selectedMonthRange?.end).format(
                                    "MMM-YYYY"
                                  )
                                    ? moment(selectedMonthRange?.end).format(
                                        "MMM-YYYY"
                                      )
                                    : ""}
                                </th>
                              </tr>
                            </thead>

                            <BootstrapTable
                              keyField="key"
                              remote
                              data={reportDatas}
                              columns={columns}
                              onTableChange={handleTableChange}
                              rowClasses={(row) => {
                                if (row?.balanceRow) {
                                  return `${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:bg-muted hover:bg-webSecondary dark:text-foreground text-white`;
                                }
                                if (row?.is_reversed) {
                                  return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                                }
                                return TRClasses;
                              }}
                              headerWrapperClasses="d-none"
                              noDataIndication="No Data Found"
                              classes={TableClasses}
                              wrapperClasses={`${TableWrapClasses} rounded-none`}
                            />
                          </table>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        ) : !org ? (
          <div className="custom-table">
            <div className={"table-body-class sm:max-h-[730px] rounded-md"}>
              <table className="w-full">
                <thead>
                  <tr className={TRClasses}>
                    {reportData && (
                      <th className={`${THClasses} text-center`}>
                        {trustData
                          ? organizationData?.organizationData?.business_name +
                            " - " +
                            (trustData?.business_name
                              ? trustData?.business_name
                              : trustData?.first_name
                              ? trustData?.first_name +
                                " " +
                                trustData?.last_name
                              : trustData?.last_name)
                          : ""}{" "}
                        Trust
                      </th>
                    )}
                  </tr>
                  <tr>
                    <th
                      className={`${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white`}
                    >
                      Ledger Account for period from{" "}
                      {moment(selectedMonthRange?.start).format("MMM-YYYY")
                        ? moment(selectedMonthRange?.start).format("MMM-YYYY")
                        : ""}{" "}
                      to{" "}
                      {moment(selectedMonthRange?.end).format("MMM-YYYY")
                        ? moment(selectedMonthRange?.end).format("MMM-YYYY")
                        : ""}
                    </th>
                  </tr>
                </thead>

                <BootstrapTable
                  keyField="key"
                  remote
                  data={reportDatas}
                  columns={columns}
                  onTableChange={handleTableChange}
                  rowClasses={(row) => {
                    if (row?.balanceRow) {
                      return `${TRClasses} font-bold dark:bg-muted bg-webSecondary dark:hover:bg-muted hover:bg-webSecondary dark:text-foreground text-white`;
                    }
                    if (row?.is_reversed) {
                      return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                    }
                    return TRClasses;
                  }}
                  headerWrapperClasses="d-none"
                  noDataIndication="No Data Found"
                  classes={TableClasses}
                  wrapperClasses={`${TableWrapClasses} rounded-none`}
                />
              </table>
            </div>
          </div>
        ) : (
          <>
            <div className="flex h-full items-center justify-center p-4">
              <Card className="max-w-md w-full shadow-lg text-center">
                <CardContent className="flex flex-col items-center gap-4 py-10">
                  <Home className="h-12 w-12 text-webPrimary" />
                  <h2 className="text-2xl font-semibold ">
                    Please Select Trust{" "}
                  </h2>
                  <p>To get Report Data.</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </MainBody>
    </>
  );
};

export default GeneralLedger;
